'use client';

import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { Video32 } from '@packages/themes/icons';
import { CliplisterFragmentFragmentDoc } from '@packages/gql/generated/shopping/CliplisterFragmentFragmentDoc';
import { dispatchDisplayOverlayTracking } from '@packages/shared/src/ui/Dialog/dispatchDialogOverlayTracking';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { messagesCliplister } from './messages';
import { CliplisterVideo } from './CliplisterVideo';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton';
import { addCliplisterScript } from './addCliplisterScript';
import { useVideoTracking } from '../Tracking/hooks/useVideoTracking';

/* GraphQL */ `
  fragment CliplisterFragment on Product {
    orderNumber
    cliplister(lang: $lang) @skip(if: $skipCliplister) {
      url
    }
  }
`;

export type CliplisterProps = {
  data: FragmentType<typeof CliplisterFragmentFragmentDoc>;
};

/**
 * <p>
 * This component is used to show product videos from cliplister.
 * </p>
 * <p>
 * It originally shows an icon. After it is clicked a `Dialog` opens, which shows a product preview image.
 * After the image is clicked, the player is appended and starts playing the product video.
 *
 * This components needs to have Cookie Consent 4 (marketing) in order to be displayed.
 * <a href="/story/docs-storybooksetup--page">Here</a> you can see how you can simulate the consent in storybook.
 * </p>
 *
 */

export const Cliplister = ({ data }: CliplisterProps) => {
  const { orderNumber, cliplister } = getFragmentData(CliplisterFragmentFragmentDoc, data);
  const dispatchGTMEvent = useTracking();
  const {
    product: {
      cliplister: { scriptUrl, consentGroup, enabled },
    },
  } = useConfig();
  // scoped two-way rendering to avoid hydration errors because of consent cookie dependecy
  const [rendered, setRendered] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [scriptLoaded, setScriptloaded] = useState(false);

  const isCookieAllowed = useCookieConsent(consentGroup);

  const { formatMessage } = useIntl();
  const { trackGlycerinDisplayVideo, trackVideoIconClick } = useVideoTracking();

  const isCliplisterAllowed = rendered && enabled && !!cliplister?.url && isCookieAllowed;
  const isCliplisterAvailable = isCliplisterAllowed && scriptLoaded;

  useEffect(() => {
    setRendered(true);
  }, []);

  useEffect(() => {
    if (isCliplisterAllowed) {
      addCliplisterScript(scriptUrl)
        .then(() => setScriptloaded(true))
        .catch(() => setScriptloaded(false));
    }
  }, [isCliplisterAllowed, scriptUrl]);

  useEffect(() => {
    if (isCliplisterAvailable) {
      trackGlycerinDisplayVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCliplisterAvailable, orderNumber]);

  if (!isCliplisterAvailable) return null;

  const openModalHandler = () => {
    trackVideoIconClick();
    setOpenDialog(true);
    dispatchDisplayOverlayTracking({
      eventDispatcher: dispatchGTMEvent,
      displayTrackingProps: {
        category: 'cliplister-dialog',
        label: formatMessage(messagesCliplister.cliplisterModalTitle),
      },
    });
  };

  return (
    <>
      <ImageGalleryCallToActionButton
        onClick={openModalHandler}
        buttonAriaLabel={formatMessage(messagesCliplister.buttonLabel)}
      >
        <Video32 sx={{ fill: (theme) => theme.palette.text.dark }} />
      </ImageGalleryCallToActionButton>
      <Dialog
        className="w-full max-w-[600px]"
        isOpen={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
      >
        <DialogHeader onClose={() => setOpenDialog(false)}>
          {formatMessage(messagesCliplister.cliplisterModalTitle)}
        </DialogHeader>
        <DialogContent>
          <CliplisterVideo orderNumber={orderNumber} />
        </DialogContent>
      </Dialog>
    </>
  );
};
